import { useNavigation, useRoute } from '@react-navigation/native';
import { Formik } from 'formik';
import get from 'lodash.get';
import sortBy from 'lodash.sortby';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, FlatList, StyleSheet, TouchableOpacity } from 'react-native';

import { HeaderGoBack } from '@components/HeaderGoBack/HeaderGoBack';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import {
  AddMemberType,
  SelectMembersList,
} from '@components/Invite/SelectMembersList';
import { WebModal } from '@components/Modals/WebModal';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import { MemberRole } from '@components/User/MemberRole';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  ListTeamsDocument,
  TeamRole,
  User,
  useAssignTeamOwnerMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMembersMutation,
} from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import useMe from '@hooks/useMe';
import { useRefetchOnFocus } from '@hooks/useRefetchOnFocus';
import useSearch from '@hooks/useSearch';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { TeamsStackScreenProps } from '@navigation/teams/teams-stack';
import TeamActionList from '@screens/Teams/TeamActionList.web';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import theme from '@themes/theme';
import {
  defaultTeamRole,
  getTeamRoleEnumValue,
  selectableTeamRoles,
} from '@utils/teams';

export type MemberAttributesWithOwner = {
  role: TeamRole | 'OWNER';
  user: User;
};

const createTeamMembersHeader = (memberCount?: number) => () => (
  <SecondaryHeader title={`Members (${memberCount})`} searchable={false} />
);

const EditRoleItemSeparator = () => <Box marginTop='m' />;
const EditRoleListFooter = () => <Box marginBottom='listFooter' />;
const EditRoleListHeader = () => <Box marginTop='m' />;

const AddMembersButton = (
  xWidth: number,
  accessibilityLabel: string,
  backgroundColor: keyof typeof theme.colors,
  txtColor: keyof typeof theme.colors,
  isLoading: boolean,
  onPress: () => void
) => {
  return (
    <TouchableOpacity onPress={onPress} accessibilityLabel={accessibilityLabel}>
      <Box
        style={{ width: xWidth, height: 48 }}
        alignItems='center'
        borderWidth={1}
        borderColor={txtColor}
        borderRadius='xs'
        justifyContent='center'
        backgroundColor={backgroundColor}
        mr='xs'>
        {!isLoading && (
          <Text variant='labelSmall' color={txtColor}>
            {accessibilityLabel}
          </Text>
        )}
        {isLoading && <ActivityIndicatorLoading />}
      </Box>
    </TouchableOpacity>
  );
};

const TeamMembers: React.FC = () => {
  const TASKTAG_SUPPORT_EMAIL = 'emmanueln@lovettcommercial.com';
  const route = useRoute<TeamsStackScreenProps<'team-members'>['route']>();

  const { me } = useMe();
  const navigation = useNavigation();
  const { search } = useSearch();
  const { t } = useTranslation('models');
  const { trackEvent } = useMixpanel();

  const { contacts } = useContacts();
  const [step, setStep] = useState<number>(1);

  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setUpgradeTeamPlanOpen,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setIsTeamPersonal,
    setTeamValues,
    setUpgradeCTA,
    teamValues,
  } = useWebDrawer();

  const goToUpgradeSubscription = () => {
    setIsTaskWebPanelOpen(false);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setUpgradeTeamPlanOpen(true);
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setUpgradeCTA(true);
    setIsTeamPersonal(team?.personal ?? false);
  };

  const {
    params: { teamUUId },
  } = route ?? { teamUUId: '' };

  // getPublicTeam, not getTeam (TA-10850): getTeam answers "The requested team
  // could not be found" for a viewer who is not a member of the team yet.
  const {
    fullTeam: team,
    loading,
    refetch,
  } = useTeamDetailFromQuery({
    teamUUId,
    fetchPolicy: 'cache-first',
  });

  useRefetchOnFocus(refetch);

  const [teamId, setTeamId] = useState<string>('');

  useEffect(() => {
    team?.id && setTeamId(team?.id);
  }, [team]);

  const [updateTeamMembers] = useUpdateTeamMembersMutation();

  // Post-success work runs in per-call onCompleted from the call's own
  // arguments: updateTeamMembers returns the Team (not the changed members)
  // and assignTeamOwner selects only its id, so neither result can be read
  // for the changed user (TA-11015). This used to live in an effect whose
  // dependency array read updateTeamMember before its declaration — a TDZ
  // ReferenceError on web.
  const updateTeamMember = (userId: string, role: TeamRole) => {
    const newRole = getTeamRoleEnumValue(role);
    updateTeamMembers({
      variables: {
        teamId,
        attributes: [{ userId, role: newRole }],
      },
      onCompleted: () => {
        trackEvent(MIXPANEL_EVENTS.TEAM_MEMBER_ROLE_CHANGED, {
          team_id: teamId,
          changed_user_id: userId,
          new_role: newRole,
        });
      },
    });
  };

  const [removeTeamMember] = useRemoveTeamMemberMutation();

  const [assignTeamOwner, { error: assignTeamOwnerError }] =
    useAssignTeamOwnerMutation({
      refetchQueries: [{ query: ListTeamsDocument }],
      // Code review fix: assignTeamOwnerError (below) already drives an
      // unconditional Alert for any error — opt out of the global
      // PERMISSION_DENIED/CONFLICT toast so this doesn't double up.
      context: { suppressGlobalMutationErrorToast: true },
    });

  useEffect(() => {
    if (!assignTeamOwnerError) return;
    Alert.alert(t('shared:error'), assignTeamOwnerError.message);
  }, [assignTeamOwnerError]);

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);

  useEffect(() => {
    if (team) {
      navigation.setOptions({
        header: createTeamMembersHeader(team?.users.length),
      });
    }
  }, [team]);

  if (loading) {
    return <ActivityIndicatorLoading />;
  }

  if (!team) {
    return (
      <Box flex={1} paddingTop='m' alignItems='center'>
        <Text>Team not found</Text>
      </Box>
    );
  }

  const { users, owner } = team;

  const initialValues = {
    users: [],
    selectedUserIds: (team?.users || []).map((u) => u.id),
  };

  const convertMembersToMembersWithRoles = (
    users: User[]
  ): MemberAttributesWithOwner[] => {
    const alphabetizedMembers = sortBy(users, [
      (item) => get(item, 'firstName')?.toLowerCase(),
      (item) => get(item, 'lastName')?.toLowerCase(),
    ]);

    const sortedOwnerFirst = alphabetizedMembers.slice();
    const ownerIndex = sortedOwnerFirst.findIndex((u) => u.id === owner?.id);
    const removeOwner = sortedOwnerFirst.splice(ownerIndex, 1)[0];
    sortedOwnerFirst.unshift(removeOwner);

    const ownerMembers: MemberAttributesWithOwner[] = [];
    const adminMembers: MemberAttributesWithOwner[] = [];
    const members: MemberAttributesWithOwner[] = [];

    sortedOwnerFirst.map((user) => {
      let role: MemberAttributesWithOwner['role'];

      if (user.id === owner?.id) {
        role = TeamRole.Owner;
        ownerMembers.push({
          user,
          role,
        });
      } else if (user.teamRoles?.includes(TeamRole.Admin)) {
        role = TeamRole.Admin;
        adminMembers.push({
          user,
          role,
        });
      } else {
        role = TeamRole.Member;
        members.push({
          user,
          role,
        });
      }

      return {
        user,
        role,
      };
    });

    return ownerMembers.concat(adminMembers).concat(members);
  };

  const memberRoleData = convertMembersToMembersWithRoles(users);

  const isOwner = users
    ?.find((u) => u.id === me?.id)
    ?.teamRoles?.includes(TeamRole.Owner);

  const isAdmin = users
    ?.find((u) => u.id === me?.id)
    ?.teamRoles?.some((role) =>
      [TeamRole.Admin, TeamRole.Owner].includes(role)
    );

  const isOwnerOrAdmin = (isOwner || isAdmin) && !team.personal;

  const removeMemberFromTeam = (userId: string) => {
    removeTeamMember({
      variables: {
        id: teamId,
        userId,
      },
    });
  };

  const assignNewOwnerAlert = (user: User) => {
    Alert.alert(
      t('models:teams.assignNewOwner.title'),
      t('models:teams.assignNewOwner.messageDowngradedToAdmin', {
        member: user.name,
      }),
      [
        { text: t('shared:cancel') },
        {
          text: t('shared:assign'),
          onPress: () => assignNewOwner(user),
        },
      ]
    );
  };

  const assignNewOwner = (user: User) => {
    if (!teamId) return;

    assignTeamOwner({
      variables: {
        id: teamId,
        newOwnerId: user.id,
      },
      onCompleted: (data) => {
        if (!data.assignTeamOwner) return;
        // The previous owner is downgraded to Admin.
        me && updateTeamMember(me.id, TeamRole.Admin);
        trackEvent(MIXPANEL_EVENTS.TEAM_MEMBER_ROLE_CHANGED, {
          team_id: teamId,
          changed_user_id: user.id,
          new_role: TeamRole.Owner,
        });
      },
    });
  };

  const renderItem = ({ item }: { item: MemberAttributesWithOwner }) => (
    <Box mx='m'>
      <MemberRole<TeamRole>
        roles={selectableTeamRoles}
        member={item}
        updateRole={
          isOwnerOrAdmin &&
          item.user.id !== owner?.id &&
          item.role !== TeamRole.Owner
            ? (role) => {
                if (role === TeamRole.Owner) {
                  assignNewOwnerAlert(item.user);
                } else {
                  updateTeamMember(item.user.id, role);
                }
              }
            : undefined
        }
        removeMember={
          isOwnerOrAdmin
            ? (member) => removeMemberFromTeam(member?.user?.id)
            : undefined
        }
        filterVal={search}
      />
    </Box>
  );

  const itemSeparatorComponent = () => <Box marginTop='m' />;
  const listFooterComponent = () => <Box marginBottom='s' />;
  const listHeaderComponent = () => <Box marginTop='xs' />;

  return (
    <Box flex={1} mx='xs'>
      <HeaderGoBack
        redirectTo='subscriptionPlanDetail'
        titleHeader='Members'
        params={{ teamUUId }}
      />
      {!team.personal && team.subscriptionDetails && (
        <TeamActionList
          teamName={team.name}
          isOwnerOrAdmin={isOwnerOrAdmin}
          setShowAddMembersModal={setShowAddMembersModal}
          teamId={teamId}
        />
      )}
      <FlatList
        data={memberRoleData}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        renderItem={renderItem}
        keyExtractor={(item, index) =>
          item.user?.id?.toString() ?? `member-${index}`
        }
        ItemSeparatorComponent={itemSeparatorComponent}
        ListFooterComponent={listFooterComponent}
        ListHeaderComponent={listHeaderComponent}
      />
      <WebModal
        accessibilityLabel='Add Team Members'
        onClose={() => setShowAddMembersModal(false)}
        visible={showAddMembersModal}
        width={504}
        height={512}>
        <Formik initialValues={initialValues} onSubmit={() => {}}>
          {({ setFieldValue, values, isSubmitting }) => {
            const { selectedUserIds: selectedUserIdsRaw, users } = values;
            const selectedUserIds = selectedUserIdsRaw?.filter(
              (s) => !!s
            ) as string[];

            if (step === 1)
              return (
                <>
                  <Box
                    flexDirection='row'
                    justifyContent='space-between'
                    marginHorizontal='m'
                    mt='l'
                    mb='xs'>
                    <Text variant='labelEmphasized'>
                      {t('shared:addMembers')}
                    </Text>
                    <Icon
                      name='X'
                      variant='m'
                      color='black'
                      onPress={() => {
                        setShowAddMembersModal(false);
                      }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    accessibilityLabel='Add Members'
                    backgroundColor='white'
                    mb='m'>
                    <SelectMembersList
                      isCreateTask={false}
                      isCreateProject={false}
                      teamUUId={teamUUId}
                      projectUUId=''
                      contacts={contacts.filter(
                        (c) => c.email !== TASKTAG_SUPPORT_EMAIL
                      )}
                      showTaskTagUsers
                      selectedUserIds={selectedUserIds}
                      disabledUsersId={selectedUserIds}
                      users={users}
                      updateUsers={(newUsers: AddMemberType[]) => {
                        setFieldValue(
                          'users',
                          newUsers.map((u) => {
                            return {
                              ...u,
                              role: u.role || defaultTeamRole,
                            };
                          })
                        );
                      }}
                    />
                    <Box
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        right: theme.spacing.m,
                      }}
                      flexDirection='row'>
                      {AddMembersButton(
                        107,
                        t('shared:next'),
                        users.length > 0 ? 'black' : 'grey02',
                        'white',
                        isSubmitting,
                        async () => {
                          if (users.length > 0) {
                            setStep(2);
                          }
                        }
                      )}
                    </Box>
                  </Box>
                </>
              );
            else if (step === 2) {
              return (
                <>
                  <Box
                    flexDirection='row'
                    justifyContent='space-between'
                    marginHorizontal='m'
                    mt='l'
                    mb='xs'>
                    <Text variant='labelEmphasized'>
                      {t('models:teams.subscriptions.editRole')}
                    </Text>
                    <Icon
                      name='X'
                      variant='m'
                      color='black'
                      onPress={() => {
                        setShowAddMembersModal(false);
                      }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    accessibilityLabel='Edit Role'
                    backgroundColor='white'>
                    <FlatList
                      data={users
                        .map((u) => {
                          return {
                            user: u,
                            role: u.role,
                          };
                        })
                        ?.filter((item) => item.role !== 'OWNER')}
                      renderItem={({ item }) => {
                        return (
                          <Box marginLeft='m' marginRight='l'>
                            <MemberRole
                              roles={selectableTeamRoles}
                              member={item}
                              updateRole={(role) => {
                                setFieldValue(
                                  'users',
                                  users.map((u) => {
                                    return {
                                      ...u,
                                      role:
                                        u.id === item.user.id ? role : u.role,
                                    };
                                  })
                                );
                              }}
                            />
                          </Box>
                        );
                      }}
                      showsHorizontalScrollIndicator={false}
                      showsVerticalScrollIndicator={false}
                      keyExtractor={(item, index) =>
                        item.user?.id ?? `member-${index}`
                      }
                      ItemSeparatorComponent={EditRoleItemSeparator}
                      ListFooterComponent={EditRoleListFooter}
                      ListHeaderComponent={EditRoleListHeader}
                    />
                    <Box style={styles.bottomButtonWeb} flexDirection='row'>
                      {AddMembersButton(
                        77,
                        t('shared:back'),
                        'white',
                        'black',
                        false,
                        () => {
                          setStep(1);
                        }
                      )}
                      {AddMembersButton(
                        110,
                        t('shared:confirm'),
                        users.length > 0 ? 'black' : 'grey02',
                        'white',
                        isSubmitting,
                        async () => {
                          if (users.length > 0) {
                            // here need to go to edit subscription to payment for the added members
                            goToUpgradeSubscription();
                            setShowAddMembersModal(false);
                            setTeamValues({ teamValues, users: users });
                          }
                        }
                      )}
                    </Box>
                  </Box>
                </>
              );
            }
          }}
        </Formik>
      </WebModal>
    </Box>
  );
};

const styles = StyleSheet.create({
  bottomButtonWeb: {
    position: 'absolute',
    bottom: theme.spacing.m,
    right: theme.spacing.m,
  },
});

export default TeamMembers;
